import { useCallback, useEffect } from 'react';
import {
  FieldArrayPath,
  FormProvider,
  useFieldArray,
  UseFieldArrayInsert,
  UseFieldArrayReturn,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { TagsFormBody } from 'src/components/TagsFormBody';
import { useAppContext } from 'src/contexts/AppContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { DeleteIcon, PlusIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { posChangedField } from 'src/utils/posFieldUtils';
import {
  PurchaseTicketsInput,
  validateTicketGroups,
} from 'src/utils/purchaseUtils';
import { ActionOutboxEntityType, TicketGroupInput } from 'src/WebApiController';

import { ListingNotesSection } from '../../../common/ListingNotesSection/ListingNotesSection';
import { useTicketGroupListingNotes } from '../../../PurchaseWizard/PurchaseTicketsFlowV2/TicketGroupInputTable/hooks/useTicketGroupListingNotes';
import { TicketGroupInputTable } from '../../../PurchaseWizard/PurchaseTicketsFlowV2/TicketGroupInputTable/TicketGroupInputTable';
import { PurchaseOrderFormDataType } from '../../MultiplePurchaseWizard';
import * as styles from '../../MultiplePurchaseWizard.css';
import { useMultiplePurchaseWizardContext } from '../../MultiplePurchaseWizardContext';
import { useCostDetailsInput } from './hooks/useCostDetailsInput';
import { usePaymentDetailsInput } from './hooks/usePaymentDetailsInput';
import { PurchaseOrderSectionHeader } from './PurchaseOrderSectionHeader';

export const PurchaseOrderSection = ({
  poIndex,
  insert,
  remove,
}: {
  poIndex: number;
  insert: UseFieldArrayInsert<PurchaseOrderFormDataType, 'purchaseOrders'>;
  remove: (index: number) => void;
}) => {
  const { getValues, setValue, formState } =
    useFormContext<PurchaseOrderFormDataType>();
  const { registerPoValidator } = useMultiplePurchaseWizardContext();
  const { loginContext } = useAppContext();

  const requiredMsg = useContent(ContentId.Required);
  const duplicateSeatMsg = useContent(ContentId.DuplicateSeat);

  const ticketGroupsFieldName: FieldArrayPath<PurchaseOrderFormDataType> = `purchaseOrders.${poIndex}.ticketGroups`;
  const purchaseOrderValues = getValues(`purchaseOrders.${poIndex}`);

  const { tags, privateNotes, currencyCode, ticketGroups, payments } =
    purchaseOrderValues;

  const currency =
    currencyCode?.value ??
    loginContext?.user?.activeAccount?.currencyCode ??
    'USD';

  const { valueOptionsContent, listingNotesMap } = useTicketGroupListingNotes({
    listingConstraints: null,
  });

  const formTicketGroups = getValues(ticketGroupsFieldName);
  const subMethods = useForm({
    defaultValues: {
      ticketGroups: formTicketGroups,
    },
  });
  const ticketGroupsFields = useFieldArray({
    control: subMethods.control,
    name: 'ticketGroups',
  });

  const validatePo = useCallback(() => {
    const tgs = subMethods.getValues('ticketGroups');
    return validateTicketGroups(
      true,
      subMethods.clearErrors,
      subMethods.setError,
      tgs,
      requiredMsg,
      duplicateSeatMsg,
      requiredMsg,
      requiredMsg,
      true
    );
  }, [duplicateSeatMsg, requiredMsg, subMethods]);

  useEffect(() => {
    registerPoValidator(validatePo);
  }, [poIndex, registerPoValidator, validatePo]);

  // Keep parent state in sync when the sub-form changes
  useEffect(() => {
    const subscription = subMethods.watch((value) => {
      if (
        !value.ticketGroups ||
        !Array.isArray(value.ticketGroups || value.ticketGroups === undefined)
      ) {
        return;
      }
      setValue(ticketGroupsFieldName, value.ticketGroups as TicketGroupInput[]);

      if (!value.ticketGroups) {
        return;
      }
    });
    return () => subscription.unsubscribe();
  }, [
    subMethods.watch,
    setValue,
    ticketGroupsFieldName,
    subMethods,
    formState.errors?.purchaseOrders,
    poIndex,
  ]);

  const { purchaseCostDialog, costDetailsTable } = useCostDetailsInput({
    ticketGroups,
    additionalCosts: purchaseOrderValues.additionalCosts,
    currency,
    index: poIndex,
  });

  const {
    editPurchasePaymentDialog,
    paymentDetailsTable,
    addPurchasePaymentDialog,
    convertCurrencyPaymentDialog,
  } = usePaymentDetailsInput({
    index: poIndex,
    currency,
    payments,
  });

  const typedTicketGroupsFields =
    ticketGroupsFields as unknown as UseFieldArrayReturn<
      PurchaseTicketsInput,
      'ticketGroups',
      'id'
    >;

  return (
    <>
      <div
        style={{
          marginBottom: vars.spacing.xl,
          border: `1px solid ${vars.color.borderLight}`,
          borderRadius: vars.borderRadius.m,
          padding: '20px 16px',
        }}
      >
        <div style={{ marginBottom: vars.spacing.xl }}>
          <Stack justifyContent="spaceBetween">
            <div
              style={{ fontSize: vars.typography.fontSize.xl, fontWeight: 500 }}
            >
              <Content id={ContentId.Purchase} /> {poIndex + 1}
            </div>
            <div>
              <Button
                variant="textPlain"
                onClick={() => {
                  const curPo = getValues(`purchaseOrders.${poIndex}`);
                  insert(poIndex + 1, { ...curPo });
                }}
              >
                <PlusIcon size={vars.iconSize.s} fill={vars.color.textBrand} />
              </Button>
              {poIndex > 0 && (
                <Button
                  variant="textPlain"
                  style={{ padding: 0 }}
                  onClick={() => {
                    remove(poIndex);
                  }}
                >
                  <DeleteIcon
                    size={vars.iconSize.s}
                    fill={vars.color.textBrand}
                  />
                </Button>
              )}
            </div>
          </Stack>
          <PurchaseOrderSectionHeader poIndex={poIndex} />
        </div>
        <FormProvider {...subMethods}>
          <TicketGroupInputTable
            purchaseTicketGroupInputField={typedTicketGroupsFields}
            valueOptionsContent={valueOptionsContent}
            listingNotesMap={listingNotesMap}
            purchaseOrderInfo={{
              id: 0,
              buyerId: '0',
              vendor: null,
              vendAcc: null,
            }}
            currencyCode={currency}
          />
        </FormProvider>
        <Stack
          style={{
            marginTop: vars.spacing.xl,
            paddingBottom: vars.spacing.xl,
            borderBottom: `1px solid ${vars.color.textPrimaryLight}`,
          }}
        >
          <div className={styles.purchaseOrderSection}>
            <div className={styles.sectionHeader}>
              <Content id={ContentId.CostAndCredits} />
            </div>
            {costDetailsTable}
          </div>

          <div className={styles.purchaseOrderSection}>
            <div className={styles.sectionHeader}>
              <Content id={ContentId.Payments} />
            </div>
            {paymentDetailsTable}
          </div>
        </Stack>
        <Stack>
          <div className={styles.purchaseOrderSection}>
            <div className={styles.sectionHeader}>
              <Content id={ContentId.Notes} />
            </div>
            <ListingNotesSection
              internalNotes={privateNotes?.value}
              onInternalNotesChanged={(notes?: string | null) =>
                setValue(
                  `purchaseOrders.${poIndex}.privateNotes`,
                  posChangedField(notes ?? null)
                )
              }
            />
          </div>

          <div className={styles.purchaseOrderSection}>
            <div className={styles.sectionHeader}>
              <Content id={ContentId.Tags} />
            </div>
            <TagsFormBody
              tagsCurrentEntity={tags?.value ?? []}
              onTagsUpdate={(tags) => {
                setValue(`purchaseOrders.${poIndex}.tags`, {
                  value: tags,
                  hasChanged: true,
                });
              }}
              entityType={ActionOutboxEntityType.Purchase}
              canEditTags={true}
            />
          </div>
        </Stack>
      </div>
      {purchaseCostDialog}
      {addPurchasePaymentDialog}
      {editPurchasePaymentDialog}
      {convertCurrencyPaymentDialog}
    </>
  );
};
